import { useCallback, useState } from 'react';

/**
 * useToggleSet is a custom hook for toggling string IDs in a Set.
 * Returns [set, toggle] where set is the Set<string> and toggle is a function to toggle an ID.
 *
 * Why is this needed?
 *
 * React state updates must be immutable, and toggling membership in a Set (e.g., for expanded/collapsed UI sections) is a common pattern that can lead to subtle bugs if you mutate the Set directly.
 *
 * This hook ensures a new Set instance is always created, so React detects the state change and re-renders as expected.
 *
 * Example scenario:
 *   - You have a list of collapsible sections (e.g., dividers in a sidebar), and you want to track which ones are open.
 *   - You store their IDs in a Set. When a user clicks a section header, you toggle its ID in the Set.
 *   - This hook provides both the Set and a toggle function, ensuring state updates are safe and efficient.
 *
 * Usage:
 *   const [openIds, toggleOpen] = useToggleSet();
 *   // ...
 *   <SectionHeader onClick={() => toggleOpen(section.id)} />
 */
export function useToggleSet(initial?: Iterable<string> | (() => Iterable<string>)) {
  const [set, setSet] = useState<Set<string>>(
    () => new Set(typeof initial === 'function' ? initial() : initial)
  );

  const toggle = useCallback((id: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return [set, toggle] as const;
}
